// Vendor Imports
import _ from 'lodash';
import isEqual from 'lodash/isEqual';
import type React from 'react';
import { useState } from 'react';

// Project Imports
import { FilterFooter } from '../FilterFooter';
import * as CheckboxFilter from '../lib/Filters/CheckboxFilter';
import { I18n } from 'common/i18n';
import { useFocusFirstActionableElement } from './InputFocus';
import { type FilterColumnsMap, type FilterEditorProps } from '../types';
import {
  type FilterValue,
  FILTER_FUNCTION,
  type SoqlFilter,
  type BinaryOperator,
  type NoopFilter,
} from '../SoqlFilter';
import { type PicklistOption } from 'common/components/Picklist';
import * as BaseFilter from '../lib/Filters/BaseFilter';
import { ForgeCheckbox, ForgeDivider, ForgeList, ForgeListItem } from '@tylertech/forge-react';

export interface CheckboxFilterEditorProps extends FilterEditorProps {
  appliedFilter: BinaryOperator | NoopFilter;
  columns: FilterColumnsMap;
}

function getSelectedValues(appliedFilter: CheckboxFilterEditorProps['appliedFilter']): FilterValue[] {
  if (appliedFilter.function !== FILTER_FUNCTION.BINARY_OPERATOR) {
    return [];
  }

  return _.map(appliedFilter.arguments, (argument) => {
    if (_.includes(['IS NULL', 'IS NOT NULL'], argument.operator)) {
      return null;
    }

    return argument.operand;
  });
}

function booleanToLocalizedString(value: FilterValue, showNullsAsFalse?: boolean) {
  if (value === true) {
    return I18n.t('shared.components.filter_bar.checkbox_filter.true_value');
  } else if (value === false) {
    return I18n.t('shared.components.filter_bar.checkbox_filter.false_value');
  } else if (showNullsAsFalse) {
    return I18n.t('shared.components.filter_bar.checkbox_filter.false_value');
  } else {
    return I18n.t('shared.components.filter_bar.checkbox_filter.no_value');
  }
}

function booleanToString(value: FilterValue) {
  if (value === true) {
    return 'true';
  } else if (value === false) {
    return 'false';
  } else {
    return null;
  }
}

function stringToBoolean(value: string) {
  if (value === 'true') {
    return true;
  } else if (value === 'false') {
    return false;
  } else {
    return null;
  }
}

export function CheckboxFilterEditor(props: CheckboxFilterEditorProps) {
  const {
    appliedFilter,
    columns,
    isReadOnly,
    onRemove,
    onUpdate,
    showNullsAsFalse,
    showRemoveButtonInFooter,
  } = props;

  const [selectedValues, setSelectedValues] = useState<FilterValue[]>(() => getSelectedValues(appliedFilter));
  const [updatedFilter, setUpdatedFilter] = useState<SoqlFilter>(appliedFilter);
  const [prevAppliedFilter, setPrevAppliedFilter] = useState(appliedFilter);
  const checkboxFilterRef = useFocusFirstActionableElement<HTMLDivElement>();

  if (!isEqual(appliedFilter, prevAppliedFilter)) {
    setPrevAppliedFilter(appliedFilter);
    setSelectedValues(getSelectedValues(appliedFilter));
    setUpdatedFilter(appliedFilter);
  }

  const updateSelectedValues = (nextSelectedValues: FilterValue[]) => {
    setSelectedValues(_.uniq(nextSelectedValues));
  };

  const onChange = (option: PicklistOption, checkedState: boolean) => {
    const value = stringToBoolean(option.value);
    if (checkedState) {
      updateSelectedValues(_.union(selectedValues, [value]));
    } else {
      updateSelectedValues(_.without(selectedValues, value));
    }
  };

  const resetFilter = () => {
    updateSelectedValues([]);
    onUpdate(BaseFilter.reset(appliedFilter, columns));
  };

  const applyFilter = () => {
    const nextUpdatedFilter = CheckboxFilter.getCheckboxFilter(appliedFilter, columns, selectedValues);
    onUpdate(nextUpdatedFilter);
    setUpdatedFilter(nextUpdatedFilter);
  };

  const isDirty = !_.isEqual(
    CheckboxFilter.getCheckboxFilter(appliedFilter, columns, selectedValues),
    updatedFilter,
  );

  const possibleOptions: PicklistOption[] = showNullsAsFalse
    ? [
        {
          // null as false option
          title: I18n.t('shared.components.filter_bar.checkbox_filter.false_value'),
          value: null,
        },
        {
          // true option
          title: I18n.t('shared.components.filter_bar.checkbox_filter.true_value'),
          value: 'true',
        },
      ]
    : [
        {
          // null option
          title: I18n.t('shared.components.filter_bar.checkbox_filter.no_value'),
          value: null,
        },
        {
          // true option
          title: I18n.t('shared.components.filter_bar.checkbox_filter.true_value'),
          value: 'true',
        },
        {
          // false option
          title: I18n.t('shared.components.filter_bar.checkbox_filter.false_value'),
          value: 'false',
        },
      ];

  const selectedOptions = _.map(selectedValues, (selectedValue) => ({
    title: booleanToLocalizedString(selectedValue, showNullsAsFalse),
    value: booleanToString(selectedValue),
  }));

  const listItems = possibleOptions.map((option) => (
    <ForgeListItem key={`option-${option.title}`} value={option.value}>
      <ForgeCheckbox
        aria-label={option.title}
        slot="start"
        checked={selectedOptions.some((selectedOption) => selectedOption.title === option.title)}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(option, event.target.checked)}
        data-testid={`${option.title}-checkbox-filter-option`}
      ></ForgeCheckbox>
      <span>{option.title}</span>
    </ForgeListItem>
  ));

  return (
    <div className="filter-controls checkbox-filter" ref={checkboxFilterRef}>
      <div className="checkbox-filter-container">
        <ForgeList dense>{listItems}</ForgeList>
        <ForgeDivider></ForgeDivider>
      </div>
      <FilterFooter
        disableApplyFilter={!isDirty}
        isDrilldown={appliedFilter.isDrilldown}
        isReadOnly={isReadOnly}
        onClickApply={applyFilter}
        onClickRemove={onRemove}
        onClickReset={resetFilter}
        showRemoveButton={showRemoveButtonInFooter}
      />
    </div>
  );
}
